import get from 'lodash.get';
import { reducer as sharedReducer, selectors as sharedSelectors } from '../shared/resultReducer';
import {
    parseTRCData,
    parseOLData,
    parseVocaboData,
    parseSpellingData,
} from '../shared/resultReducerParsers';
import {
    removeExistingResults,
    getStatuses,
    setResultsAndStatuses,
    setAdditionalStatuses,
    buildStatus,
} from '../../../utils/shared/resultReducerHelper';
import {
    FETCH_SHARED_RESULTS,
    SAVE_BASIC_COMPREHENSION_RESULT,
} from '../../actionTypes/shared/apiActionTypes';
import {
    FETCH_BURST_RESULTS_FOR_YEAR,
    FETCH_READING_RESULTS,
    FETCH_TRO_AUDIO_DATA,
    FETCH_D8_RESULT_BY_ID,
    INVALIDATE_D8_RESULT,
    PATCH_TRO_REMARKS,
    REMOVE_D8_RESULT_AFTER_INVALIDATION,
    REVERT_TRO_BOOK,
    INVALIDATE_D8_SECOND_ATTEMPT_RESULTS,
} from '../../actionTypes/reading/apiActionTypes';
import { D8_MEASURES_WITH_LEXILE } from '../../../constants/reading/MeasuresConstants';
import { resultStateProperties } from '../../../constants/reading/CommonConstants';
import { LECTURA_MEASURES } from '../../../constants/shared/MeasuresConstants';
import {
    updateTroResultsStatuses,
    updateBurstResultsForYear,
    mergeStudentData,
    getTroResult,
} from '../../../utils/reading/resultReducerHelper';
import {
    getD8Data,
    getD8Result,
    parseDecodingData,
    parseR3DData,
    parseRanData,
    parseTroData,
    updateStudentObject,
    parseLecturaData,
    parseDecodeResultsData,
} from './resultReducerParsers';

export const initialState = {
    data: {},
    resultToInvalidate: null,
    areResultsUpdating: false,
    areRemarksUpdating: false,
    isAllResultsFetched: false,
    isAdditionalResultsFetched: false,
    error: null,
    isTroAudioFetching: false,
};

export const reducer = (state = initialState, action = {}) => {
    switch (action.type) {
        case FETCH_SHARED_RESULTS.R3D:
            return {
                ...state,
                data: mergeStudentData(state.data, parseR3DData(action.response)),
            };
        case FETCH_SHARED_RESULTS.DECODE:
            return {
                ...state,
                data: mergeStudentData(state.data, parseDecodeResultsData(action.response)),
            };
        case FETCH_SHARED_RESULTS.TRC_ENG:
            return {
                ...state,
                data: mergeStudentData(
                    state.data,
                    parseTRCData(action.response, resultStateProperties, setResultsAndStatuses),
                ),
            };
        case FETCH_SHARED_RESULTS.TRC_SPA:
            return {
                ...state,
                data: mergeStudentData(
                    state.data,
                    parseTRCData(action.response, resultStateProperties, setAdditionalStatuses),
                ),
            };
        case FETCH_SHARED_RESULTS.OL_ENG:
            return {
                ...state,
                data: mergeStudentData(
                    state.data,
                    parseOLData(action.response, resultStateProperties),
                ),
            };
        case FETCH_READING_RESULTS.TRO:
            return {
                ...state,
                data: mergeStudentData(state.data, parseTroData(action.response)),
            };
        case FETCH_READING_RESULTS.DECODING:
            return {
                ...state,
                data: mergeStudentData(state.data, parseDecodingData(action.response)),
            };
        case FETCH_SHARED_RESULTS.VOCABO_ENG:
            return {
                ...state,
                data: mergeStudentData(
                    state.data,
                    parseVocaboData(action.response, resultStateProperties, setResultsAndStatuses),
                ),
            };
        case FETCH_SHARED_RESULTS.VOCABO_SPA:
            return {
                ...state,
                data: mergeStudentData(
                    state.data,
                    parseVocaboData(action.response, resultStateProperties, setAdditionalStatuses),
                ),
            };
        case FETCH_SHARED_RESULTS.SPELLING:
            return {
                ...state,
                data: mergeStudentData(
                    state.data,
                    parseSpellingData(action.response, resultStateProperties),
                ),
            };
        case FETCH_READING_RESULTS.RAN:
            return {
                ...state,
                data: mergeStudentData(state.data, parseRanData(action.response)),
            };
        case FETCH_SHARED_RESULTS.D8:
            return {
                ...state,
                data: mergeStudentData(
                    removeExistingResults(state.data, 'statuses', D8_MEASURES_WITH_LEXILE),
                    getD8Data(action.response),
                ),
                areResultsUpdating: false,
            };
        case FETCH_SHARED_RESULTS.LECTURA:
            return {
                ...state,
                data: mergeStudentData(
                    removeExistingResults(state.data, 'additionalStatuses', LECTURA_MEASURES),
                    parseLecturaData(action.response),
                ),
                areResultsUpdating: false,
            };
        case SAVE_BASIC_COMPREHENSION_RESULT.SUCCESS:
            return {
                ...state,
                data: updateStudentObject(
                    state.data,
                    action.additionalResponseParams.studentId,
                    [getD8Result(action.response)],
                    getStatuses(action.response, false, buildStatus),
                ),
            };
        case FETCH_TRO_AUDIO_DATA.START: {
            return {
                ...state,
                isTroAudioFetching: true,
            };
        }
        case FETCH_TRO_AUDIO_DATA.SUCCESS: {
            return {
                ...state,
                isTroAudioFetching: false,
                data: updateTroResultsStatuses(state.data, {
                    ...action.additionalResponseParams,
                    troAudioSrc: action.response,
                }),
            };
        }
        case PATCH_TRO_REMARKS.SUCCESS: {
            const { bookId, troAudioSrc } = action.additionalResponseParams;
            const parsedResult = getTroResult(action.response);
            const { studentId, resultId } = parsedResult.result;

            return {
                ...state,
                areResultsUpdating: false,
                areRemarksUpdating: false,
                data: updateTroResultsStatuses(state.data, {
                    studentId,
                    resultId,
                    bookId,
                    troAudioSrc,
                    parsedResult,
                }),
            };
        }
        case FETCH_D8_RESULT_BY_ID.SUCCESS:
        case REMOVE_D8_RESULT_AFTER_INVALIDATION: {
            return {
                ...state,
                resultToInvalidate: action.response,
            };
        }
        case FETCH_TRO_AUDIO_DATA.ERROR:
        case PATCH_TRO_REMARKS.ERROR:
        case REVERT_TRO_BOOK.ERROR:
        case INVALIDATE_D8_SECOND_ATTEMPT_RESULTS.ERROR:
        case INVALIDATE_D8_RESULT.ERROR:
        case FETCH_D8_RESULT_BY_ID.ERROR:
            return { ...state, error: action.error };
        case FETCH_BURST_RESULTS_FOR_YEAR.SUCCESS:
            return {
                ...state,
                data: updateBurstResultsForYear(state.data, action.response),
            };
        case PATCH_TRO_REMARKS.START:
        case REVERT_TRO_BOOK.START:
            return { ...state, areRemarksUpdating: true, areResultsUpdating: true };
        case INVALIDATE_D8_RESULT.START:
        case INVALIDATE_D8_SECOND_ATTEMPT_RESULTS.START:
            return {
                ...state,
                areResultsUpdating: true,
            };
        default:
            return sharedReducer(state, action);
    }
};

const getYearsStudentResultsFetchedFor = (state, studentId) =>
    get(state.data[studentId], 'fetchedYears', []);

const isTroRemarksUpdating = state => state.areRemarksUpdating;
const isTroAudioFetching = state => state.isTroAudioFetching;
const getResultToInvalidate = state => state.resultToInvalidate;

export const selectors = {
    ...sharedSelectors,
    isTroRemarksUpdating,
    isTroAudioFetching,
    getYearsStudentResultsFetchedFor,
    getResultToInvalidate,
};
